import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { tap, map } from 'rxjs/operators';

export interface Language {
  code: string;
  name: string;
  flag: string;
  direction: string;
}

export interface Translations {
  [key: string]: { [key: string]: string };
}

export interface LanguageData {
  languages: Language[];
  translations: Translations;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private currentLanguageSubject = new BehaviorSubject<string>('en');
  private languagesSubject = new BehaviorSubject<Language[]>([]);
  private translationsSubject = new BehaviorSubject<Translations>({});

  public currentLanguage$ = this.currentLanguageSubject.asObservable();
  public languages$ = this.languagesSubject.asObservable();
  public translations$ = this.translationsSubject.asObservable();

  constructor(private http: HttpClient) {
    this.loadLanguages();
    this.loadSavedLanguage();
  }

  private loadLanguages(): void {
    this.http.get<LanguageData>('languages.json').subscribe({
      next: (data) => {
        this.languagesSubject.next(data.languages);
        this.translationsSubject.next(data.translations);
        // Ensure document attributes are set after languages load
        const currentLang = this.currentLanguageSubject.value;
        this.updateDocumentAttributes(currentLang);
      },
      error: (error) => {
        console.error('Error loading languages:', error);
        // Fallback to default on error
        this.updateDocumentAttributes('en');
      }
    });
  }

  private loadSavedLanguage(): void {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage) {
      this.currentLanguageSubject.next(savedLanguage);
      // Update document attributes for saved language (will update after languages load)
      this.updateDocumentAttributes(savedLanguage);
    } else {
      // No saved language - ensure default 'en' is set and saved to localStorage
      this.currentLanguageSubject.next('en');
      localStorage.setItem('selectedLanguage', 'en');
      // Document attributes will be set after languages load in loadLanguages()
    }
  }

  private updateDocumentAttributes(languageCode: string): void {
    const language = this.languagesSubject.value.find(lang => lang.code === languageCode);
    if (language) {
      document.documentElement.dir = language.direction;
      document.documentElement.lang = languageCode;
    } else {
      // Fallback to default if language not loaded yet
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = languageCode || 'en';
    }
  }

  setLanguage(languageCode: string): void {
    this.currentLanguageSubject.next(languageCode);
    localStorage.setItem('selectedLanguage', languageCode);
    // Update document attributes
    this.updateDocumentAttributes(languageCode);
  }

  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  getLanguages(): Language[] {
    return this.languagesSubject.value;
  }

  getTranslations(): Translations {
    return this.translationsSubject.value;
  }

  translate(key: string, params?: { [key: string]: string | number }): string {
    const currentLang = this.currentLanguageSubject.value;
    const translations = this.translationsSubject.value;
    
    if (!translations || !translations[currentLang]) {
      return key;
    }
    
    let translation = translations[currentLang][key] || translations['en']?.[key] || key;
    
    // Replace parameters in the translation
    if (params) {
      Object.keys(params).forEach(paramKey => {
        const placeholder = `{${paramKey}}`;
        translation = translation.replace(placeholder, params[paramKey].toString());
      });
    }
    
    return translation;
  }

  // Reactive translation method that returns an Observable
  translate$(key: string, params?: { [key: string]: string | number }): Observable<string> {
    return combineLatest([
      this.currentLanguage$,
      this.translations$
    ]).pipe(
      map(([currentLang, translations]) => {
        if (!translations || !translations[currentLang]) {
          return key;
        }
        
        let translation = translations[currentLang][key] || translations['en']?.[key] || key;
        
        // Replace parameters in the translation
        if (params) {
          Object.keys(params).forEach(paramKey => {
            const placeholder = `{${paramKey}}`;
            translation = translation.replace(placeholder, params[paramKey].toString());
          });
        }
        
        return translation;
      })
    );
  }

  // Alias for translate method for easier use in templates
  t(key: string, params?: { [key: string]: string | number }): string {
    return this.translate(key, params);
  }
} 